import { useMemo, useState } from 'react';
import format from 'date-fns/format';
import { addMonths, min, startOfDay, subDays, subMonths } from 'date-fns';
import { createFileRoute } from '@tanstack/react-router';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS, ReportStatus } from '@repo/shared/domain';
import { useQuery } from '@tanstack/react-query';
import { createAsyncReport, getAsyncReports, getAsyncReportDownloadUrl } from 'src/api/admin/reports';
import { getSites } from 'src/api/admin/sites';
import { Alert, Button } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import enGB from 'date-fns/locale/en-GB';
import { DataGrid, FilterAutoCompleteById } from '@repo/components';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ReportStatusChip } from '@repo/components';

interface SearchParams {
	page?: number;
	sort?: string;
}
export const Route = createFileRoute('/_admin/reports/occupancy')({
	component: OccupancyReportPage,
	validateSearch: (search: Record<string, unknown>): SearchParams => {
		return {
			sort: search.sort as string | undefined,
			page: search.page as number | undefined,
		};
	},
});

//TODO display a warning when report has been generated on the same date as the end date.
const columns = [
	{
		field: 'name',
		headerName: 'File name',
		sortable: false,
		flex: 3,
	},
	{
		field: 'createdAt',
		headerName: 'Generated At',
		sortable: false,
		flex: 1,
		renderCell: (params: { value: string }) => format(new Date(params.value), 'dd/MM/yyyy HH:mm'),
	},
	{
		field: 'status',
		headerName: 'Status',
		sortable: false,
		flex: 1,
		renderCell: (params: { value: ReportStatus }) => <ReportStatusChip status={params.value} />,
	},
];
function OccupancyReportPage() {
	const [generateParams, setGenerateParams] = useState<{
		startDate: Date | null;
		endDate: Date | null;
		siteId?: string;
	}>({
		startDate: (() => {
			const now = new Date();
			const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
			return firstDayPrevMonth;
		})(),
		endDate: (() => {
			const now = new Date();
			const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
			return lastDayPrevMonth;
		})(),
	});

	const yesterday = useMemo(() => subDays(startOfDay(new Date()), 1), []);

	const maxEndDate =
		generateParams.startDate != null ? min([yesterday, addMonths(startOfDay(generateParams.startDate), 3)]) : yesterday;
	const minStartDate = generateParams.endDate != null ? subMonths(startOfDay(generateParams.endDate), 3) : undefined;
	const maxStartDate = generateParams.endDate != null ? min([generateParams.endDate, yesterday]) : yesterday;

	const { user: authenticatedUser } = useAuthenticatedUser();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasPermission = verifyPermission([PERMISSIONS.adminReportsView]);
	useBreadcrumbLevel(0, 'Reports');
	useBreadcrumbLevel(1, 'Occupancy');

	const { data: sitesData, isLoading: isLoadingSites } = useQuery({
		queryKey: ['sites-list'],
		queryFn: () => getSites('noPagination=true'),
		enabled: hasPermission,
	});

	const sites = sitesData?.data || [];

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['reports-occupancy', search.page || 0],
		queryFn: () => getAsyncReports('occupancy', { page: search.page || 0 }),
	});

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleDateFromChange = (date: Date | null) => {
		if (!date || isNaN(date.getTime())) {
			setGenerateParams({ ...generateParams, startDate: null });
			return;
		}
		const updates: Partial<typeof generateParams> = { startDate: date };
		if (
			generateParams.endDate &&
			(date > generateParams.endDate || generateParams.endDate > addMonths(startOfDay(date), 3))
		) {
			updates.endDate = null;
		}
		setGenerateParams({ ...generateParams, ...updates });
	};
	const handleDateToChange = (date: Date | null) => {
		if (!date || isNaN(date.getTime())) {
			setGenerateParams({ ...generateParams, endDate: null });
			return;
		}
		if (!date) return;
		setGenerateParams({ ...generateParams, endDate: date });
	};
	const handleRowClick = async ({ row }: { id: string; row: Report }) => {
		if (row.status === 'completed') {
			const url = await getAsyncReportDownloadUrl('occupancy', row.id);
			window.open(url, '_blank');
		}
	};

	const generateReport = async () => {
		if (!generateParams.startDate || !generateParams.endDate) {
			return;
		}
		try {
			await createAsyncReport('occupancy', {
				startDate: generateParams.startDate,
				endDate: generateParams.endDate,
				siteId: generateParams.siteId,
			});
		} catch (error) {
			console.error(error);
		} finally {
			refetch();
		}
	};

	const isDateRangeTooLong =
		!!generateParams.startDate &&
		!!generateParams.endDate &&
		generateParams.endDate > addMonths(startOfDay(generateParams.startDate), 3);
	const isInvalidDateRange =
		!!generateParams.startDate &&
		!!generateParams.endDate &&
		(generateParams.startDate > generateParams.endDate || generateParams.endDate > yesterday || isDateRangeTooLong);
	const generationDisabled =
		isLoadingSites ||
		isLoading ||
		!generateParams.siteId ||
		!generateParams.startDate ||
		!generateParams.endDate ||
		isInvalidDateRange;
	return (
		<>
			<div className="px-4 pt-4 flex flex-col flex-wrap gap-2 w-full justify-between items-center pb-4">
				{data?.data?.some((report) => report.status === 'pending') && (
					<Alert
						severity="info"
						className="w-full"
						action={
							<Button color="inherit" size="small" onClick={() => refetch()}>
								Refresh
							</Button>
						}
					>
						A report is being generated. This may take a few minutes.
					</Alert>
				)}
				<div
					className="flex flex-col gap-3 w-full"
					style={{
						border: '1px solid #e0e0e0',
						borderRadius: 8,
						padding: 16,
						backgroundColor: '#fcfcfc',
					}}
				>
					<Alert severity="info" variant="outlined">
						{' '}
						This report shows how many people were on site each date during the date range. The date range must not
						exceed three months.
					</Alert>
					<div className="flex flex-row flex-wrap gap-2 items-start">
						<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
							<DatePicker
								value={generateParams.startDate}
								onChange={handleDateFromChange}
								minDate={minStartDate}
								maxDate={maxStartDate}
								slotProps={{
									textField: {
										size: 'small',
										sx: { flex: '1 1 150px', backgroundColor: '#fff' },
									},
								}}
							/>
							<DatePicker
								value={generateParams.endDate}
								onChange={handleDateToChange}
								minDate={generateParams.startDate || undefined}
								maxDate={maxEndDate}
								slotProps={{
									textField: {
										size: 'small',
										sx: { flex: '1 1 150px', backgroundColor: '#fff' },
									},
								}}
							/>
						</LocalizationProvider>
						<FilterAutoCompleteById
							key="siteFilter"
							label="Filter Sites*"
							required={true}
							disabled={isLoadingSites}
							options={sites}
							value={generateParams.siteId}
							onChange={(siteId) => setGenerateParams({ ...generateParams, siteId: siteId as string })}
							size="small"
							sx={{ flex: '1 1 200px', maxWidth: 300 }}
						/>
					</div>
					<div className="flex justify-end">
						<Button
							variant="contained"
							color="primary"
							disabled={generationDisabled}
							onClick={generateReport}
							sx={{ width: 250 }}
						>
							Generate Report
						</Button>
					</div>
				</div>
			</div>
			<div className="p-4 pt-0 mt-4">
				<DataGrid
					data={data?.data || []}
					columns={columns}
					rowCount={data?.count || 0}
					pageSize={data?.pageSize || 50}
					page={data?.page || 0}
					isLoading={isLoading}
					onPaginationModelChange={handlePaginationModelChange}
					paginationMode="server"
					onRowClick={handleRowClick}
					sortingMode="server"
					sortingOrder={['asc', 'desc']}
					pagination
					onReload={refetch}
					getRowClassName={() => '!cursor-default'}
				/>{' '}
			</div>
		</>
	);
}
